import { z } from 'zod';

import { RequestStatus } from './requests/status.js';
import { RussiaRequestAuditAction } from './russia-requests.js';

/**
 * @aiPurpose wire projection of a `RussiaRequestAuditEntry` ledger row.
 *   Actor is always present — the underlying FK is `Restrict`, so a
 *   User cannot be hard-deleted while audit rows reference it (operators
 *   soft-delete via `User.active = false`). `from` / `to` are only
 *   populated for transition rows; mutations leave them as `null`.
 * @aiPii surfaces the actor's name — redact in logs.
 */
export const RussiaRequestAuditEntrySchema = z
  .object({
    id: z.string().uuid(),
    at: z.string().datetime(),
    action: z.nativeEnum(RussiaRequestAuditAction),
    actor: z
      .object({
        id: z.string().uuid(),
        name: z.string().min(1),
      })
      .strict(),
    from: z.nativeEnum(RequestStatus).nullable(),
    to: z.nativeEnum(RequestStatus).nullable(),
  })
  .strict();
export type RussiaRequestAuditEntry = z.infer<typeof RussiaRequestAuditEntrySchema>;

/**
 * @aiPurpose query for `GET /api/russia-requests/:id/audit-entries`.
 *   Cursor pagination on `(at, id)` desc — newest first.
 */
export const RussiaRequestAuditEntriesQuerySchema = z
  .object({
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict();
export type RussiaRequestAuditEntriesQuery = z.infer<typeof RussiaRequestAuditEntriesQuerySchema>;

/**
 * @aiPurpose paginated audit-entries response.
 */
export const RussiaRequestAuditEntriesResponseSchema = z
  .object({
    items: z.array(RussiaRequestAuditEntrySchema),
    nextCursor: z.string().nullable(),
  })
  .strict();
export type RussiaRequestAuditEntriesResponse = z.infer<
  typeof RussiaRequestAuditEntriesResponseSchema
>;
